import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { compressImage } from '../utils/compressImage';
import { Lock, MapPin, Phone, MessageCircle, Camera, ClipboardList, Lightbulb, Loader2, Sparkles } from 'lucide-react';
import logger from '../utils/logger';

const CreateService = () => {
  const { user } = useContext(AuthContext);
  const [form, setForm] = useState({
    titre: '',
    description: '',
    tarif: '',
    categorie: 'other',
    delai: '',
    telephone: '',
    whatsapp: '',
    localisation: ''
  });
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingLimit, setCheckingLimit] = useState(true);
  const [error, setError] = useState('');
  const [limitError, setLimitError] = useState(null);
  const navigate = useNavigate();

  // Limite globale de services pour tous les utilisateurs
  const MAX_SERVICES = 7;

  const categories = [
    { id: 'commerce', label: 'Commerce' },
    { id: 'artisanat', label: 'Artisanat' },
    { id: 'services', label: 'Services' },
    { id: 'dev', label: 'Développement' },
    { id: 'design', label: 'Design' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'transport', label: 'Transport' },
    { id: 'restauration', label: 'Restauration' },
    { id: 'beaute', label: 'Beauté & Bien-être' },
    { id: 'education', label: 'Éducation & Formation' },
    { id: 'other', label: 'Autre' }
  ];

  useEffect(() => {
    // Vérifier que l'utilisateur est auteur
    if (user && user.role !== 'author' && user.role !== 'admin') {
      navigate('/services');
      return;
    }
    
    // Vérifier la limite de services
    checkServiceLimit();
  }, [user, navigate]);

  const checkServiceLimit = async () => {
    try {
      const servicesRes = await api.get('/services/my');
      const currentServices = servicesRes.data.services?.length || 0;
      
      if (currentServices >= MAX_SERVICES) {
        setLimitError({
          type: 'limit_reached',
          message: `Vous avez atteint la limite de ${MAX_SERVICES} services. Supprimez un service existant pour en créer un nouveau.`,
          currentServices,
          limit: MAX_SERVICES
        });
      }
    } catch (err) {
      logger.error(err);
    } finally {
      setCheckingLimit(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePhotoChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      setError('Maximum 5 photos autorisées');
      return;
    }
    // Compresser chaque photo
    const compressed = await Promise.all(
      files.map(f => compressImage(f, { maxSizeMB: 1 }).catch(() => f))
    );
    setPhotos(compressed);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Créer le service
      const res = await api.post('/services', form);
      const serviceId = res.data.id;
      
      // Si des photos sont sélectionnées, les uploader
      if (photos.length > 0) {
        const formData = new FormData();
        photos.forEach(photo => {
          formData.append('photos[]', photo);
        });
        
        try {
          await api.post(`/services/${serviceId}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (photoErr) {
          logger.error('Erreur upload photos:', photoErr);
          // Ne pas bloquer la création si les photos échouent
        }
      }
      
      // Rediriger vers Mes Services
      navigate('/my-services', { 
        state: { 
          message: 'Service créé ! Pour le rendre visible, activez un abonnement.',
          serviceId: serviceId 
        }
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création du service');
    } finally {
      setLoading(false);
    }
  };

  // Écran de chargement
  if (checkingLimit) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Écran de blocage si limite atteinte
  if (limitError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 py-10 px-4">
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-amber-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-800 mb-4">
              Limite de services atteinte
            </h1>
            
            <p className="text-gray-600 mb-6">{limitError.message}</p>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="flex justify-center gap-8 text-sm">
                <div>
                  <p className="text-gray-500">Services créés</p>
                  <p className="text-2xl font-bold text-gray-800">{limitError.currentServices}</p>
                </div>
                <div className="border-l border-gray-200"></div>
                <div>
                  <p className="text-gray-500">Limite maximale</p>
                  <p className="text-2xl font-bold text-emerald-600">{limitError.limit}</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <p className="text-blue-800 text-sm flex items-center gap-1">
                <Lightbulb className="w-4 h-4" /> <strong>Supprimez un service existant</strong> pour pouvoir en créer un nouveau.
              </p>
            </div>
            
            <div className="space-y-3">
              <Link
                to="/my-services"
                className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition"
              >
                <ClipboardList className="w-5 h-5" /> Gérer mes services
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-emerald-700 mb-2 text-center">Créer un service</h1>
        <p className="text-center text-gray-600 mb-8">
          Proposez vos compétences ou produits sur la marketplace
        </p>
        
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8">
          <div className="space-y-5">
            {/* Titre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre du service *</label>
              <input
                type="text"
                name="titre"
                value={form.titre}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                placeholder="Ex: Couture sur mesure, Plomberie, Cours d'anglais..."
              />
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                required
                rows={4}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
                placeholder="Décrivez votre service en détail : ce que vous proposez, votre expérience, etc."
              />
            </div>
            
            {/* Catégorie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
              <select
                name="categorie"
                value={form.categorie}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>
            
            {/* Tarif et Délai */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tarif (XOF) *</label>
                <input
                  type="number"
                  name="tarif"
                  value={form.tarif}
                  onChange={handleChange}
                  required
                  min="0"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  placeholder="5000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Délai de livraison</label>
                <input
                  type="text"
                  name="delai"
                  value={form.delai}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  placeholder="Ex: 24h, 3-5 jours, Sur RDV..."
                />
              </div>
            </div>

            {/* Localisation */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><MapPin className="w-4 h-4" /> Localisation</label>
              <input
                type="text"
                name="localisation"
                value={form.localisation}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                placeholder="Ex: Abidjan, Cocody | Bouaké Centre | Livraison partout en CI"
              />
            </div>

            {/* Contact */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><Phone className="w-4 h-4" /> Téléphone</label>
                <input
                  type="tel"
                  name="telephone"
                  value={form.telephone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  placeholder="+225 07 00 00 00"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><MessageCircle className="w-4 h-4" /> WhatsApp</label>
                <input
                  type="tel"
                  name="whatsapp"
                  value={form.whatsapp}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  placeholder="+225 07 00 00 00"
                />
              </div>
            </div>

            {/* Photos */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><Camera className="w-4 h-4" /> Photos (max 5)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
              {photos.length > 0 && (
                <p className="text-sm text-gray-500 mt-1">{photos.length} photo(s) sélectionnée(s)</p>
              )}
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Création...</> : <><Sparkles className="w-5 h-5" /> Créer le service</>}
          </button>
          
          <p className="text-center text-sm text-gray-500 mt-4">
            Après création, vous pourrez activer votre service depuis "Mes Services"
          </p>
        </form>
      </div>
    </div>
  );
};

export default CreateService;

